<script setup lang="ts">
  import Avatar from '@leaflink/stash/Avatar.vue';
  import Badge from '@leaflink/stash/Badge.vue';
  import Dropdown from '@leaflink/stash/Dropdown.vue';
  import Icon from '@leaflink/stash/Icon.vue';
  import Menu from '@leaflink/stash/Menu.vue';
  import MenuItem from '@leaflink/stash/MenuItem.vue';
  import { storeToRefs } from 'pinia';
  import { computed, ref } from 'vue';
  import { RouteLocationRaw } from 'vue-router';

  import { SPLIT_NAMES } from '@/constants';
  import { ROUTE_NAMES } from '@/constants/routeNames';
  import { Company } from '@/models/company';
  import { useAuthStore } from '@/piniaStores/auth/auth.store';
  import { type InternalUserState } from '@/piniaStores/user/user.state';
  import { useUserStore } from '@/piniaStores/user/user.store';
  import { i18n } from '@/plugins';
  import { trackNotificationPreferencesClick } from '@/segment';

  interface ReferralLinkData {
    icon: 'link' | 'money';
    linkText: string;
    route: RouteLocationRaw;
  }

  const props = defineProps<{
    user: InternalUserState;
    company: Company;
  }>();

  const emits = defineEmits<{
    (e: 'open-share-menu-modal'): void;
  }>();

  const authStore = useAuthStore();
  const isDropdownOpen = ref(false);
  const userStore = storeToRefs(useUserStore());
  const isSplitEnabled = userStore.getIsSplitEnabled;

  const notificationPreferencesLink = { name: ROUTE_NAMES.NOTIFICATION_PREFERENCES };
  const featuresLink = { name: ROUTE_NAMES.LEAFLINK_FEATURES };
  const profileLink = { name: ROUTE_NAMES.PROFILE_EDIT };

  const referralLinkData = computed<ReferralLinkData>(() => {
    return isSplitEnabled.value(SPLIT_NAMES.ADS_ENABLE_VUEIFIED_SHARE_LEAFLINK)
      ? {
          icon: 'money',
          linkText: i18n.t('layout.userProfile.referRetailer'),
          route: { name: ROUTE_NAMES.SHARE_LEAFLINK },
        }
      : {
          icon: 'link',
          linkText: i18n.t('layout.userProfile.shareLeafLink'),
          route: { name: ROUTE_NAMES.SHAREABLE_LINKS },
        };
  });

  const headshot = computed(() => props.user?.headshot || '');
  const fullName = computed(() => `${props.user.firstName} ${props.user.lastName}`);
  const isReferralProgram = computed(() => userStore.user.value.featureFlags.referralProgramActive);
  const displayShareLink = computed(() => {
    if (!props.company) {
      return false;
    }

    const { isDeliveryProvider, seller: isSeller } = props.company;

    return isReferralProgram.value && isSeller && !isDeliveryProvider;
  });
  const displayNewHighlights = computed(() => {
    const { featuresCount = 0, featureFlags } = props.user;

    return featuresCount > 0 && featureFlags.showFeatureHighlights;
  });
  const displayFeatureHighlights = computed(() => props.user.featureFlags.showFeatureHighlights);
  const isNotificationPreferencesEnabled = isSplitEnabled.value(SPLIT_NAMES.KNOCK_NOTIFICATIONS);
  const isSeller = computed(() => userStore.user.value.isSeller);
  const isShareMenuEnabled = computed(() => isSplitEnabled.value(SPLIT_NAMES.ADS_ENABLE_SHARE_MENU) && isSeller.value);
</script>

<template>
  <Dropdown
    class="tw-mr-1"
    :content-class="$style.content"
    :reattach="false"
    @toggle="isDropdownOpen = !isDropdownOpen"
  >
    <template #toggle="{ toggle }">
      <button :class="['rounded-full relative', $style.avatar]" @click="toggle">
        <Avatar
          color="purple"
          shade="main"
          data-test="avatar-toggle|user-profile"
          :alt="fullName"
          :class="[{ [$style['focus-badge']]: isDropdownOpen }]"
          :name="fullName"
          :src="headshot"
        />
        <span v-show="displayNewHighlights" :class="[$style.dot, 'border border-white absolute rounded-full']"></span>
      </button>
    </template>

    <template #default>
      <div :class="[$style['user-info'], 'bg-blue-100 border-b border-blue pa-2 flex align-center']">
        <Avatar
          data-test="avatar|user-profile"
          class="mr-2"
          color="purple"
          shade="main"
          :alt="fullName"
          :name="fullName"
          :src="headshot"
        />
        <div>
          <div data-test="fullname|user-profile" class="heading-4 text-ice-900">{{ fullName }}</div>
          <div data-test="email|user-profile" class="small text-ice-900">{{ user.email }}</div>
        </div>
      </div>
      <Menu :class="$style.nav">
        <MenuItem>
          <router-link :to="profileLink">
            <Icon name="user" />
            {{ i18n.t('layout.userProfile.manageProfile') }}
          </router-link>
        </MenuItem>
        <MenuItem v-if="isNotificationPreferencesEnabled">
          <router-link
            :to="notificationPreferencesLink"
            @click="trackNotificationPreferencesClick({ access_point: 'User Profile' })"
          >
            <Icon name="alert-bell" />
            {{ i18n.t('layout.userProfile.notificationPreferences') }}
          </router-link>
        </MenuItem>
        <MenuItem v-if="displayShareLink">
          <router-link :to="referralLinkData.route">
            <Icon :name="referralLinkData.icon" />
            {{ referralLinkData.linkText }}
          </router-link>
        </MenuItem>
        <MenuItem v-if="isShareMenuEnabled" @click="() => emits('open-share-menu-modal')">
          <div>
            <Icon name="paper-plane" />
            {{ i18n.t('layout.userProfile.shareMenu.linkText') }}
          </div>
        </MenuItem>
        <MenuItem v-if="displayFeatureHighlights">
          <router-link :to="featuresLink">
            <Badge :content="user.featuresCount" position="inline">
              <Icon name="logo-ll" />
              {{ i18n.t('layout.userProfile.releaseNotes') }}
            </Badge>
          </router-link>
        </MenuItem>
        <MenuItem>
          <button @click="authStore.logout()">
            <Icon name="logout" />
            {{ i18n.t('layout.userProfile.logout') }}
          </button>
        </MenuItem>
      </Menu>
    </template>
  </Dropdown>
</template>

<style lang="scss" module>
  .dot {
    background: var(--color-red);
    height: 10px;
    right: -1px;
    top: 2px;
    width: 10px;
  }

  .avatar {
    cursor: pointer;
    max-height: space(4);
    max-width: space(4);

    &:hover::before {
      border-radius: 50%;
      bottom: 0;
      box-shadow: 0 0 0 1px var(--color-blue);
      content: '';
      display: block;
      left: 0;
      position: absolute;
      right: 0;
      top: 0;
      z-index: 0;
    }

    &:focus {
      outline: none;
    }
  }

  .focus-badge {
    box-shadow: 0 0 0 5px var(--color-blue-100);
  }

  .content {
    margin-right: 0;
    margin-top: 6px;
  }

  .nav {
    a:hover {
      background-color: var(--color-ice-200);
      color: var(--color-ice-700);
    }
  }

  .user-info {
    border-top-left-radius: $border-radius;
    border-top-right-radius: $border-radius;
  }
</style>
